import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Users, ClipboardList, AlertTriangle, CalendarClock } from "lucide-react";
import { StatCard, AdherenceRing, EmptyState, PageLoading, buttonVariants, cn } from "@dosa/ui";
import { useAuth } from "../../hooks/useAuth";
import { listAssignedPatients } from "../../lib/api-client";

export function PharmacistDashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const pharmacistId = user!.pharmacistId!;

  const { data: patients, isLoading } = useQuery({
    queryKey: ["assigned-patients", pharmacistId],
    queryFn: () => listAssignedPatients(pharmacistId),
  });

  if (isLoading) return <PageLoading />;

  return (
    <div className="space-y-11">
      <h1 className="text-[2.25rem] font-semibold leading-none text-charcoal-900">{t("pharmacist.dashboard.title")}</h1>

      <div className="grid grid-cols-2 items-end gap-6 sm:grid-cols-4">
        <StatCard value={patients?.length ?? 0} label={t("pharmacist.dashboard.assignedPatients")} icon={<Users className="h-4 w-4" />} />
        <StatCard value={0} label={t("pharmacist.dashboard.followUpsDue")} icon={<CalendarClock className="h-4 w-4" />} />
        <StatCard
          value={0}
          label={t("pharmacist.dashboard.pendingInterventions")}
          icon={<ClipboardList className="h-4 w-4" />}
          valueClassName="text-clinical-high"
        />
        <StatCard value={0} label={t("pharmacist.dashboard.openAssessments")} icon={<AlertTriangle className="h-4 w-4" />} valueClassName="text-charcoal-400" />
      </div>

      <div className="flex flex-wrap items-start gap-11">
        <div className="min-w-0 flex-[1_1_420px]">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-700">
            {t("pharmacist.dashboard.patientQueue")}
          </p>
          {!patients || patients.length === 0 ? (
            <EmptyState title={t("pharmacist.patients.empty")} />
          ) : (
            <ul>
              {patients.map((p) => (
                <li key={p.id} className="flex items-baseline gap-4 border-t border-charcoal-900/[0.14] py-4">
                  <div className="flex-1">
                    <p className="text-lg font-semibold text-charcoal-900">
                      {p.firstName} {p.lastName}
                    </p>
                    <p className="mt-0.5 text-[13.5px] text-charcoal-500">{p.patientCode}</p>
                  </div>
                  <Link to={`/pharmacist/patients/${p.id}`} className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}>
                    {t("pharmacist.patientProfile.startAssessment")}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex-[0_1_300px]">
          <p className="mb-3.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-700">
            {t("pharmacist.dashboard.clinicAdherence")}
          </p>
          <AdherenceRing percent={81} label={t("pharmacist.dashboard.clinicAdherence")} />
          <p className="mt-4 max-w-[260px] text-[14.5px] leading-relaxed text-charcoal-500">
            {t("pharmacist.dashboard.adherenceNote")}
          </p>
        </div>
      </div>
    </div>
  );
}
